import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { LoadingState, ErrorState } from '@/components/ui/data-state'
import { FormModal } from '@/components/ui/form-modal'
import { NumberFieldControl } from '@/components/ui/form-fields'

import { useCount, useCountActions, useStockItemsById } from '../hooks'
import type { CountLine, CountLineWarning, StockCount, StockItem } from '../types'
import { countWarningLabels, quantity, variance } from './inventory-format'

const QUANTITY_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }

interface CountSheetDialogProps {
  countId: string | null
  onOpenChange: (isOpen: boolean) => void
}

/**
 * The count sheet: one line per item, what is on the shelf typed in the item's
 * own unit. Saving sends only the lines that changed; a line cleared goes back
 * to not counted — which is not zero, and is left alone when the count posts.
 */
export function CountSheetDialog({ countId, onOpenChange }: CountSheetDialogProps) {
  const { count, isLoading, error, retry } = useCount(countId)

  if (countId === null) return null

  if (!count) {
    return (
      <FormModal
        isOpen
        onOpenChange={onOpenChange}
        title="Count sheet"
        submitLabel="Save counts"
        onSubmit={(event) => event.preventDefault()}
        isBusy
      >
        {isLoading ? (
          <LoadingState />
        ) : (
          <ErrorState subject="this count" error={error} onRetry={retry} />
        )}
      </FormModal>
    )
  }

  return <CountSheet key={count.id} count={count} onOpenChange={onOpenChange} />
}

interface CountSheetProps {
  count: StockCount
  onOpenChange: (isOpen: boolean) => void
}

type SheetValues = { lines: Array<{ qty?: number }> }

function CountSheet({ count, onOpenChange }: CountSheetProps) {
  const { enterLine, refreshCounts } = useCountActions()
  const isOpen = count.status === 'draft' || count.status === 'counting'
  /** What saving each line answered: its expected and variance (when shown) and any warning. */
  const [answers, setAnswers] = useState<ReadonlyMap<string, CountLine>>(new Map())

  const items = useStockItemsById(useMemo(() => count.lines.map((line) => line.itemId), [count]))
  const { control, handleSubmit } = useForm<SheetValues>({
    defaultValues: {
      lines: count.lines.map((line) => ({ qty: line.countedQty ?? undefined })),
    },
  })
  const values = useWatch({ control, name: 'lines' })

  const submit = handleSubmit(async (form) => {
    const next = new Map(answers)
    for (const [index, line] of count.lines.entries()) {
      const qty = form.lines[index]?.qty
      const before = line.countedQty ?? undefined
      if (qty === before) continue

      const answer = await enterLine.mutateAsync({
        countId: count.id,
        itemId: line.itemId,
        body: { entries: qty === undefined ? [] : [{ qty }] },
      })
      next.set(line.itemId, answer)
    }
    setAnswers(next)
    await refreshCounts()
  })

  const counted = values.filter((line) => line?.qty !== undefined).length

  return (
    <FormModal
      isOpen
      onOpenChange={onOpenChange}
      title={isOpen ? 'Count sheet' : 'Count'}
      description={
        isOpen
          ? `${counted} of ${count.lines.length} lines counted. Leave a line empty if it was not counted.${count.blind ? ' Blind: expected quantities stay hidden until the count is posted.' : ''}`
          : 'This count is no longer open.'
      }
      submitLabel={isOpen ? 'Save counts' : 'Close'}
      onSubmit={
        isOpen
          ? submit
          : (event) => {
              event.preventDefault()
              onOpenChange(false)
            }
      }
      isBusy={enterLine.isPending}
      error={enterLine.error}
      errorFallback="Could not save a line."
      dialogClassName="max-w-3xl"
      scroll="inside"
    >
      <div className="divide-border flex flex-col divide-y">
        {count.lines.map((line, index) => (
          <SheetLine
            key={line.id}
            line={answers.get(line.itemId) ?? line}
            item={items.get(line.itemId)}
            index={index}
            control={control}
            isEditable={isOpen}
            showExpected={!count.blind || count.status === 'posted'}
          />
        ))}
      </div>
    </FormModal>
  )
}

interface SheetLineProps {
  line: CountLine
  item: StockItem | undefined
  index: number
  control: ReturnType<typeof useForm<SheetValues>>['control']
  isEditable: boolean
  showExpected: boolean
}

function SheetLine({ line, item, index, control, isEditable, showExpected }: SheetLineProps) {
  const unit = item?.unitOfMeasure ?? ''

  return (
    <div className="grid items-end gap-3 py-3 sm:grid-cols-[minmax(0,1fr)_180px]">
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">{item?.name ?? '…'}</span>
        <span className="text-muted text-xs tabular-nums">
          {showExpected && line.expectedQty !== null
            ? `Expected ${quantity(line.expectedQty)} ${unit}${line.varianceQty !== null ? ` · difference ${variance(line.varianceQty)} ${unit}` : ''}`
            : line.state === 'counted'
              ? 'Counted'
              : 'Not counted'}
        </span>
        {line.warning && <Warning warning={line.warning} />}
      </div>

      {isEditable ? (
        <NumberFieldControl
          control={control}
          name={`lines.${index}.qty` as const}
          label={`Counted${unit ? ` (${unit})` : ''}`}
          minValue={0}
          formatOptions={QUANTITY_FORMAT}
          isCompact
        />
      ) : (
        <span className="text-right tabular-nums">
          {line.countedQty === null ? '—' : `${quantity(line.countedQty)} ${unit}`}
        </span>
      )}
    </div>
  )
}

function Warning({ warning }: { warning: CountLineWarning }) {
  return (
    <span role="status" className="text-warning text-xs">
      {countWarningLabels[warning]}
    </span>
  )
}
